/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/VerificationCodeMenuActions.tsx
 * FILE_ID: EMAILHUB-CONTEXTMENU-VERIFICATION-ACTIONS-001
 * PURPOSE: Generic right-click actions for an owner-provided verification code.
 * WHY: Centralizes resolver-gated clipboard and redacted feedback behavior.
 */
import { Copy, ShieldAlert } from 'lucide-react'

import { ContextMenuItem } from '../../../_internal/components/ContextMenu'
import {
  rejectVerificationSignal,
  type EmailActionSignal,
  writeCodeToClipboard,
} from '../../action-signals'
import type { Email } from '../../hooks/useEmails'

interface VerificationCodeMenuActionsProps {
  email: Pick<Email, 'id'>
  signal: EmailActionSignal | null
  onClose: () => void
  toast: (
    message: string,
    kind?: 'success' | 'error' | 'warning' | 'info',
  ) => void
}

export function VerificationCodeMenuActions({
  email,
  signal,
  onClose,
  toast,
}: VerificationCodeMenuActionsProps) {
  if (!signal) return null

  const copy = async () => {
    try {
      await writeCodeToClipboard(signal.display_value)
      onClose()
      toast('Verification code copied.', 'success')
    } catch {
      toast(
        "Couldn't copy the verification code. Check clipboard permission and try again.",
        'error',
      )
    }
  }

  const reject = async () => {
    try {
      await rejectVerificationSignal(email.id)
      onClose()
      toast('Marked as not a verification code. The code itself was not saved.', 'success')
    } catch {
      toast('Could not record that correction. Check your connection and try again.', 'error')
    }
  }

  return (
    <>
      <ContextMenuItem icon={Copy} onClick={() => void copy()}>
        Copy verification code
      </ContextMenuItem>
      <ContextMenuItem icon={ShieldAlert} onClick={() => void reject()}>
        Not a code
      </ContextMenuItem>
    </>
  )
}
